import type { BookData, Status } from './types';
import { statusName } from './model';

// Book page Part 2 (Phase 6.1; decisions doc › "From the build: book page Part 2, moves and
// marks", Kristi Oct 27): what the Move ▾ menu offers, the words for each change, and the
// change applied to the page's copy of the book so it shows at once. The database functions
// (0025: book_move, book_mark, book_set, undo_action) are the record; these only mirror them.

export type MoveTo = 'up_next' | 'future_reads' | 'reference' | 'recently_added' | 'not_for_me';
export type Mark = 'favorite' | 'read_again' | 'try_again';
export type SetField = 'interest' | 'read_type' | 'why_read' | 'recommended_by' | 'notes';
export type NotForMeKind = 'abandoned' | 'passed_on';

export interface MoveDetails {
  kind?: NotForMeKind;
  /** Up Next and Future reads: kept place (default) · top · bottom · a number (Kristi, Oct 27). */
  place?: { place: string };
  chips?: string[];
  reason?: string;
}

/** Interest hints (Kristi, Oct 26), beside the dots and as each dot's tooltip. */
export const INTEREST_HINTS = ['not very interested', 'mildly interested', 'interested', 'very interested', 'can’t wait!!'] as const;
export const interestHint = (n: number | null) => (n === null ? 'Interest not set' : `${n} · ${INTEREST_HINTS[n - 1]}`);

/** Quick reasons on the Not for me… sheet (board 4's Stop reading reasons, less the loan ones). */
export const NOT_FOR_ME_CHIPS = ['Not in the mood for this kind', 'Too long for now', 'Too heavy right now', 'Lost interest', 'Not what I expected'];

/** Goodreads ratings: exact under 1,000; "4.5k" to 9,999; "112k"; "1.2M" (Kristi, Oct 27). */
export function ratingsCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(Math.floor(n / 100) / 10).toString()}k`;
  if (n < 1_000_000) return `${Math.floor(n / 1000)}k`;
  return `${(Math.floor(n / 100_000) / 10).toString()}M`;
}

/** "★ 4.08 · 112k ratings" (or just the score while the count isn't known). */
export function goodreadsLine(score: number, count: number | null): string {
  return `★ ${score.toFixed(2)}${count !== null ? ` · ${ratingsCount(count)} ${count === 1 ? 'rating' : 'ratings'}` : ''}`;
}

/** Abandoned when there's a stopped read since the last finish; otherwise Passed on (as book_move). */
export function notForMeKindOf(book: Pick<BookData, 'reads'>): NotForMeKind {
  const lastFinish = book.reads
    .filter((r) => r.outcome === 'finished')
    .map((r) => r.created_at ?? '')
    .sort()
    .at(-1);
  return book.reads.some((r) => r.outcome === 'stopped' && (!lastFinish || (r.created_at ?? '') > lastFinish)) ? 'abandoned' : 'passed_on';
}

export interface MoveChoice {
  /** A plain move (book_move); null when the choice opens a sheet or isn't possible. */
  to: MoveTo | null;
  /** Part 3: the sheet this choice opens instead (Start reading, Stop reading, Mark finished). */
  opens: 'start' | 'stop' | 'finish' | 'past' | null;
  /** With opens 'stop': which Stop reading choice starts picked. */
  stopTo?: 'future_reads' | 'up_next' | 'not_for_me';
  label: string;
  note: string;
  here: boolean;
}

const MOVE_ORDER: { to: MoveTo | null; label: string; status: Status | null | 'none' }[] = [
  { to: null, label: 'Currently Reading', status: 'currently_reading' },
  { to: 'up_next', label: 'Up Next', status: 'up_next' },
  { to: 'future_reads', label: 'Future reads', status: 'future_reads' },
  { to: null, label: 'Finished…', status: 'finished' },
  { to: 'not_for_me', label: 'Not for me…', status: 'not_for_me' },
  { to: 'reference', label: 'Reference', status: 'reference' },
  // No "No shelf yet" here (Kristi, Oct 28): a book leaves No shelf yet for a shelf, never back.
];

/**
 * The Move ▾ menu for this book: every shelf, the current one marked (Part 2). From Part 3, a
 * book being read goes through Stop reading (Up Next, Future reads, Not for me) or Mark
 * finished; Currently Reading opens Start reading.
 */
export function moveChoices(book: Pick<BookData, 'status' | 'reads'>, opts: { pastRead?: boolean } = {}): MoveChoice[] {
  const reading = book.reads.some((r) => r.outcome === 'open');
  // Finished… on a book you're not reading adds a past read, which needs edit mode: without it the
  // choice is hidden, not promised (R11, Kristi Nov 1).
  const order = MOVE_ORDER.filter((m) => m.status !== 'finished' || reading || opts.pastRead || book.status === 'finished');
  return order.map((m) => {
    const here = m.status === book.status;
    if (here) return { to: null, opens: null, label: m.label.replace('…', ''), note: 'Here now ✓', here: true };
    if (m.status === 'currently_reading') return { to: null, opens: 'start', label: m.label, note: 'Start reading', here: false };
    if (m.status === 'finished')
      return reading
        ? { to: null, opens: 'finish', label: m.label, note: 'date, stars', here: false }
        : { to: null, opens: 'past', label: m.label, note: 'add a past read', here: false };
    if (reading && (m.to === 'up_next' || m.to === 'future_reads' || m.to === 'not_for_me'))
      return { to: null, opens: 'stop', stopTo: m.to, label: m.label, note: 'Stop reading…', here: false };
    if (reading) return { to: null, opens: null, label: m.label, note: 'Stop reading or Mark finished first', here: false };
    const note = m.to === 'not_for_me' ? 'asks why' : m.to === 'up_next' || m.to === 'future_reads' ? 'asks where' : '';
    return { to: m.to, opens: null, label: m.label, note, here: false };
  });
}

/** The Undo notice's words for a move. */
export function moveMessage(title: string, to: MoveTo, kind?: NotForMeKind): string {
  const where = to === 'not_for_me' ? `Not for me (${kind === 'abandoned' ? 'Abandoned' : 'Passed on'})` : statusName(to === 'recently_added' ? null : to);
  return `${title} moved to ${where}`;
}

export const MARK_NAMES: Record<Mark, string> = { favorite: '♥ Favorite', read_again: '↻ Read again', try_again: '↺ Try again' };

const local = (kind: string, from: Status | null, to: Status | null, now: string) => ({
  id: `local-${kind}-${now}`,
  happened_at: now,
  kind,
  from_status: from,
  to_status: to,
  place: 'book_page',
  read_id: null,
});

/** The page's copy of the book after a move (book_move's rules). */
export function applyMove(book: BookData, to: MoveTo, details: MoveDetails, now: string, today: string): BookData {
  const target: Status | null = to === 'recently_added' ? null : to;
  const history = [...book.history];
  let next: BookData = { ...book, status: target };
  if (to === 'not_for_me') {
    const kind = details.kind ?? notForMeKindOf(book);
    next = { ...next, tryAgainAt: null, notForMe: { kind, on: today, where: 'book_page', chips: details.chips ?? [] } };
    history.unshift(local(kind === 'passed_on' ? 'passed_on' : 'moved', book.status, target, now));
    if (book.tryAgainAt) history.unshift(local('try_again_cleared', book.status, target, now));
  } else {
    history.unshift(local(book.status === 'not_for_me' ? 'brought_back' : 'moved', book.status, target, now));
  }
  return { ...next, history };
}

/** The page's copy of the book after ♥ ↻ ↺. */
export function applyMark(book: BookData, mark: Mark, on: boolean, now: string): BookData {
  const at = on ? now : null;
  if (mark === 'favorite') return { ...book, favoriteAt: at };
  const history = [local(`${mark}_${on ? 'set' : 'cleared'}`, book.status, book.status, now), ...book.history];
  return mark === 'read_again' ? { ...book, readAgainAt: at, history } : { ...book, tryAgainAt: at, history };
}

/** The page's copy of the book after setting one of its fields. Empty words are no words. */
export function applySet(book: BookData, field: SetField, value: number | string | null, readTypes: { name: string; description: string | null }[] = []): BookData {
  const words = typeof value === 'string' ? value.trim() || null : null;
  switch (field) {
    case 'interest':
      return { ...book, interest: typeof value === 'number' ? value : null };
    case 'read_type':
      return { ...book, readType: words ? (readTypes.find((r) => r.name.toLowerCase() === words.toLowerCase()) ?? { name: words, description: null }) : null };
    case 'why_read':
      return { ...book, whyRead: words };
    case 'recommended_by':
      return { ...book, recommendedBy: words };
    case 'notes':
      return { ...book, notes: words };
  }
}

/** The Undo notice's words for a set. */
export function setMessage(field: SetField, value: number | string | null): string {
  const words = typeof value === 'string' ? value.trim() : value;
  switch (field) {
    case 'interest':
      return words === null ? 'Interest cleared' : `Interest set to ${interestHint(Number(words))}`;
    case 'read_type':
      return words ? `Read type set to ${words}` : 'Read type cleared';
    case 'why_read':
      return words ? 'Why I want to read it saved' : 'Why I want to read it cleared';
    case 'recommended_by':
      return words ? 'Recommended by saved' : 'Recommended by cleared';
    case 'notes':
      return words ? 'Note about the book saved' : 'Note about the book cleared';
  }
}

/** What Undo says, given undo_action's answer. */
export function undoMessage(title: string, kept: string[]): string {
  if (!kept.length) return `Undone: ${title} is as it was`;
  const names = [...new Set(kept.map((k) => FIELD_WORDS[k] ?? k.replace(/_/g, ' ')))];
  return `Undone, except ${names.join(', ')}: changed since, so kept`;
}

const FIELD_WORDS: Record<string, string> = {
  interest: 'interest',
  read_type_id: 'read type',
  status: 'shelf',
  favorite_at: '♥ Favorite',
  read_again_at: '↻ Read again',
  try_again_at: '↺ Try again',
  why_read: 'why I want to read it',
  recommended_by: 'recommended by',
  notes: 'notes',
  not_for_me_reason: 'your Not for me words',
  not_for_me_chips: 'the Not for me reasons',
  book_private: 'your own words',
  book_positions: 'its place on the shelf',
  history: 'its History step',
};
